import type { ReactNode } from 'react'
import { taxonomies, type EntityType } from '@datahub/shared'
import { ExternalLink } from '../../components/ExternalLink'
import { withCurrency } from '../ticket-size'

/** The record columns the Overview card reads. */
export interface EntityRecord {
  entityType: EntityType
  legalCompanyName: string
  status: string
  timezone: string
  sector: string | null
  countryOfIncorporation: string | null
  websiteUrl: string | null
  employeeCount: string | null
  typeIfBanking: string | null
  ownershipStatus: string | null
  investorType: string[]
  investorStatus: string | null
  legalStructure: string | null
  ticketType: string | null
  hqCityCountry: string | null
  hqCountry: string | null
  hqRegion: string | null
  countriesOfOperation: string[]
  baseCurrency: string | null
  assetsUnderManagement: string | number | null
  totalCommitted: string | number | null
  regionsOfInvestment: string[]
  sectorPreferences: string[]
  investmentStages: string[]
  ticketSizes: string[]
  /** The currency the ticket sizes are quoted in, from the profile or from Stears. Not a column. */
  ticketSizeCurrency: string | null
  owner: { displayName: string | null; email: string } | null
}

/**
 * The record columns worth showing for this kind of entity.
 *
 * For a fund, the card leaves out what the "Corporate Data" card of the profile below
 * already shows (F5). This is the name, the investor or LP type, the investor status or
 * legal structure, and the website. For a GP, the headquarters is the city and country
 * that the GP form asks for. There is no separate headquarters country. The countries of
 * operation follow it (F3).
 */
export function recordRows(company: EntityRecord): [string, ReactNode][] {
  const name = (slug: string | null | undefined, taxonomy: string): string | null =>
    slug ? ((taxonomies[taxonomy] ?? []).find((t) => t.slug === slug)?.name ?? slug) : null
  const names = (slugs: string[], taxonomy: string): string[] =>
    slugs.map((s) => name(s, taxonomy)).filter((n): n is string => Boolean(n))
  const list = (slugs: string[], taxonomy: string): string => names(slugs, taxonomy).join(', ') || '-'
  const figure = (value: string | number | null): string => {
    if (value === null) return '-'
    const n = Number(value)
    if (!Number.isFinite(n)) return '-'
    return `${company.baseCurrency ? `${company.baseCurrency} ` : ''}${n.toLocaleString('en-GB')}`
  }
  const owner = company.owner?.displayName ?? company.owner?.email ?? '-'
  const website: ReactNode = company.websiteUrl ? <ExternalLink value={company.websiteUrl} /> : '-'

  if (company.entityType === 'company') {
    return [
      ['Legal name', company.legalCompanyName],
      ['Status', company.status],
      ['Sector', name(company.sector, 'sectors') ?? '-'],
      ['Country of incorporation', name(company.countryOfIncorporation, 'countries') ?? '-'],
      ['Website', website],
      ['Ownership status', company.ownershipStatus ?? '-'],
      ['Employees', company.employeeCount ?? '-'],
      ['Bank or non-bank', company.typeIfBanking ?? '-'],
      ['Timezone', company.timezone],
      ['Owner', owner],
    ]
  }

  const isGp = company.entityType === 'gp'
  // A Stears-linked GP can have a country and no city, so the country fills the one row.
  const headquarters = company.hqCityCountry ?? name(company.hqCountry, 'countries') ?? '-'
  const rows: [string, ReactNode][] = [['Status', company.status]]
  if (isGp) {
    rows.push(
      ['Headquarters (city, country)', headquarters],
      ['HQ region', name(company.hqRegion, 'hq_regions') ?? '-'],
      ['Countries of operation', list(company.countriesOfOperation, 'countries')],
    )
  } else {
    // F3 is for the GP form only. The LP form asks for a country and no city.
    rows.push(['Head office', headquarters], ['HQ region', name(company.hqRegion, 'hq_regions') ?? '-'])
  }
  rows.push(
    ['Assets under management', figure(company.assetsUnderManagement)],
    [isGp ? 'Capital committed' : 'Committed to funds', figure(company.totalCommitted)],
    ['Regions of investment', list(company.regionsOfInvestment, 'regions')],
    ['Sector preferences', list(company.sectorPreferences, 'investment_sectors')],
    ['Stages', list(company.investmentStages, 'investment_stages')],
    ['Ticket sizes', withCurrency(names(company.ticketSizes, 'ticket_sizes'), company.ticketSizeCurrency).join(', ') || '-'],
  )
  if (!isGp) rows.push(['Ticket type', name(company.ticketType, 'lp_ticket_types') ?? '-'])
  rows.push(['Timezone', company.timezone], ['Owner', owner])
  return rows
}

/** The names of the countries the record rows show, so another card does not repeat them. Only a GP shows them. */
export function countryNamesShown(company: Pick<EntityRecord, 'entityType' | 'countriesOfOperation'>): string[] {
  if (company.entityType !== 'gp') return []
  return company.countriesOfOperation.map((slug) => (taxonomies['countries'] ?? []).find((t) => t.slug === slug)?.name ?? slug)
}
